import React, { useState, useEffect } from "react";
import {
  Home, Users as UsersIcon, Briefcase, TrendingUp, BarChart3, ArrowUpRight, ClipboardList,
  Upload, Settings, Ticket, Scale, Database, Loader2, ShieldCheck, Ruler, Target,
} from "lucide-react";
import { Icon } from "./icons.jsx";
import { useRoute, navigate, REDIRECCIONES } from "./router.jsx";
import { Panel } from "./screens/panel.jsx";
import { FichaEmpresa } from "./screens/ficha-empresa.jsx";
import { AppTopbar } from "./topbar.jsx";
import { SessionProvider, useIgualdad } from "./igualdad-store.jsx";
import { IgualdadShell } from "./igualdad-shell.jsx";
import { IgualdadLogin } from "./screens/igualdad-login.jsx";
import { IgualdadInicio } from "./screens/igualdad-inicio.jsx";
import { IgualdadPersonas, IgualdadPuestos } from "./screens/igualdad-plantilla.jsx";
import { IgualdadPersonaFicha } from "./screens/igualdad-persona-ficha.jsx";
import { IgualdadRegistro } from "./screens/igualdad-registro.jsx";
import { IgualdadEstructura, IgualdadAnalytics } from "./screens/igualdad-analitica.jsx";
import { IgualdadIncrementos } from "./screens/igualdad-incrementos.jsx";
import { IgualdadBandasPuesto, IgualdadPosicionamiento } from "./screens/igualdad-bandas.jsx";
import { IgualdadEvaluaciones } from "./screens/igualdad-evaluaciones.jsx";
import { IgualdadCarga } from "./screens/igualdad-carga.jsx";
import { IgualdadCatalogos } from "./screens/igualdad-catalogos.jsx";
import { IgualdadConsolidacion } from "./screens/igualdad-consolidacion.jsx";
import { IgualdadConfiguracion } from "./screens/igualdad-configuracion.jsx";
import { IgualdadTickets } from "./screens/igualdad-tickets.jsx";
import { Empresas } from "./screens/empresas.jsx";
import { SvptProvider, useStore as useSvpt } from "./svpt/store.jsx";
import { SvptPage } from "./svpt/pagina.jsx";

/* ============================================================
   Talention — App shell + simple hash router
   ============================================================ */

/* --- Navegación, agrupada por dominio funcional (no por app de origen) --- */
const NAV = [
  {
    label: "General",
    items: [
      { id: "/dashboard", icon: "dashboard", name: "Dashboard" },
      { id: "/igualdad", iconC: Home, name: "Inicio retribución" },
      { id: "/entity", icon: "building", name: "Ficha de empresa" },
    ],
  },
  {
    label: "Organización",
    items: [
      { id: "/igualdad/puestos", iconC: Briefcase, name: "Puestos" },
      { id: "/igualdad/personas", iconC: UsersIcon, name: "Personas" },
    ],
  },
  {
    label: "Sistema de Valoración de Puestos",
    items: [
      { id: "/svpt/dashboard", icon: "dashboard", name: "Dashboard" },
      { id: "/svpt/orgchart", icon: "sitemap", name: "Organigrama" },
      { id: "/svpt/departments", icon: "users", name: "Departamentos" },
      { id: "/svpt/levels", icon: "tree", name: "Niveles jerárquicos" },
      { id: "/svpt/positions", icon: "briefcase", name: "Puestos" },
      { id: "/svpt/factors", icon: "sliders", name: "Factores" },
      { id: "/svpt/scales", icon: "scale", name: "Escalas" },
      { id: "/svpt/valuation", icon: "check-list", name: "Valorar puestos" },
    ],
  },
  {
    label: "Retribución",
    items: [
      { id: "/igualdad/registro", iconC: Scale, name: "Registro retributivo", badge: "UE" },
      { id: "/igualdad/estructura", iconC: TrendingUp, name: "Políticas retributivas" },
      { id: "/igualdad/analytics", iconC: BarChart3, name: "People Analytics" },
      { id: "/igualdad/bandas", iconC: Ruler, name: "Bandas salariales" },
      { id: "/igualdad/posicionamiento", iconC: Target, name: "Posicionamiento en banda" },
      { id: "/igualdad/incrementos", iconC: ArrowUpRight, name: "Incrementos salariales" },
      { id: "/igualdad/evaluaciones", iconC: ClipboardList, name: "Evaluaciones" },
    ],
  },
  {
    label: "Datos y administración",
    items: [
      { id: "/igualdad/carga", iconC: Upload, name: "Carga masiva" },
      { id: "/igualdad/consolidacion", iconC: ShieldCheck, name: "Consolidación del Registro" },
      { id: "/igualdad/catalogos", iconC: Database, name: "Catálogos" },
      { id: "/igualdad/config", iconC: Settings, name: "Configuración" },
      { id: "/igualdad/tickets", iconC: Ticket, name: "Tickets" },
    ],
  },
];

/* Identidad del usuario autenticado, con salida de sesion. */
function UserCard() {
  const { session, logout } = useIgualdad();
  const nombre = session.user?.name || "Usuario";
  const iniciales = nombre.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "··";
  const rol = session.user?.roles?.[0] || (session.user?.es_staff ? "Staff" : "Usuario");

  function salir() {
    logout();
    navigate("/dashboard");
  }

  return (
    <div className="user-card">
      <div className="avatar">{iniciales}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="user-name">{nombre}</div>
        <div className="user-role">{rol}</div>
      </div>
      <button className="icon-btn" style={{ width: 28, height: 28 }} title="Cerrar sesión" onClick={salir}>
        <Icon name="settings" size={14} />
      </button>
    </div>
  );
}

function Sidebar({ route, go }) {
  const { session } = useIgualdad();

  const permisos = session.user?.permisos || [];
  const esStaff = Boolean(session.user?.es_staff);
  const puedeValorar = esStaff || permisos.includes("valoraciones.view");

  // Oculta el apartado SVPT a quien no tiene permiso sobre valoraciones.
  const grupos = NAV
    .map((g) => g.label === "Sistema de Valoración de Puestos" && !puedeValorar ? { ...g, items: [] } : g)
    .filter((g) => g.items.length > 0);

  // Badges del SVPT embebido, solo con su espacio ya cargado: el sidebar no
  // dispara la carga perezosa. Sin provider (tests sueltos) useSvpt es null.
  const svpt = useSvpt();
  const svptListo = svpt?.carga === "listo";
  const svptFactoresMal = svptListo && svpt.state.metodo === "talention" && svpt.state.factors.groups.reduce((s, g) => s + g.svpt, 0) !== 100;
  const svptPorRevisar = svptListo ? svpt.state.positions.filter((p) => p.review).length : 0;
  const svptPendientes = svptListo ? svpt.state.positions.filter((p) => p.status !== "FINALIZADA").length : 0;

  // Un hijo marca activo a su padre: /svpt/valuation/3 activa /svpt/valuation.
  const isActive = (id) => route === id || (id !== "/igualdad" && route.startsWith(id + "/"));

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">T</div>
        <div>
          <div className="brand-name">Talention</div>
          <div className="brand-sub">Gestión Laboral</div>
        </div>
      </div>

      {grupos.map(group => (
        <div key={group.label}>
          <div className="nav-group-label">{group.label}</div>
          <div className="nav-list">
            {group.items.map(item => (
              <button
                key={item.id}
                className={"nav-item " + (isActive(item.id) ? "active" : "")}
                onClick={() => go(item.id)}
              >
                <span className="icon">
                  {item.iconC
                    ? <item.iconC size={16} strokeWidth={2} />
                    : <Icon name={item.icon} size={17} />}
                </span>
                <span>{item.name}</span>
                {item.badge && <span className="badge">{item.badge}</span>}
                {item.id === "/svpt/factors" && svptFactoresMal && <span className="badge warn">!</span>}
                {item.id === "/svpt/orgchart" && svptPorRevisar > 0 && <span className="badge warn">{svptPorRevisar}</span>}
                {item.id === "/svpt/valuation" && svptPendientes > 0 && <span className="badge">{svptPendientes}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}

      <UserCard />
    </aside>
  );
}

function Page({ route }) {
  const destino = REDIRECCIONES[route];
  useEffect(() => {
    if (destino) navigate(destino);
  }, [destino]);
  if (destino) return null;

  if (route.startsWith("/empresas"))              return <Empresas />;
  // El apartado SVPT resuelve todo /svpt/*.
  if (route.startsWith("/svpt"))                  return <SvptPage route={route} />;
  // La ficha va antes que la lista: si no, el prefijo se la comeria.
  if (route.startsWith("/igualdad/personas/")) {
    const id = parseInt(route.split("/")[3], 10);
    return <IgualdadPersonaFicha personaId={id} />;
  }
  if (route.startsWith("/igualdad/personas"))     return <IgualdadPersonas />;
  if (route.startsWith("/igualdad/puestos"))      return <IgualdadPuestos />;
  if (route.startsWith("/igualdad/registro"))     return <IgualdadRegistro />;
  if (route.startsWith("/igualdad/estructura"))   return <IgualdadEstructura />;
  if (route.startsWith("/igualdad/analytics"))    return <IgualdadAnalytics />;
  if (route.startsWith("/igualdad/bandas"))       return <IgualdadBandasPuesto />;
  if (route.startsWith("/igualdad/posicionamiento")) return <IgualdadPosicionamiento />;
  if (route.startsWith("/igualdad/incrementos"))  return <IgualdadIncrementos />;
  if (route.startsWith("/igualdad/evaluaciones")) return <IgualdadEvaluaciones />;
  if (route.startsWith("/igualdad/carga"))        return <IgualdadCarga />;
  if (route.startsWith("/igualdad/consolidacion")) return <IgualdadConsolidacion />;
  if (route.startsWith("/igualdad/catalogos"))    return <IgualdadCatalogos />;
  if (route.startsWith("/igualdad/config"))       return <IgualdadConfiguracion />;
  if (route.startsWith("/igualdad/tickets"))      return <IgualdadTickets />;
  if (route.startsWith("/igualdad"))              return <IgualdadInicio />;
  if (route.startsWith("/dashboard")) return <Panel />;
  if (route.startsWith("/entity")) return <FichaEmpresa />;
  return <Panel />;
}

/* Aplicacion autenticada: solo se monta cuando hay sesion. */
function AppAutenticada() {
  const [route, go] = useRoute();

  return (
    <SvptProvider>
      <div className="app">
        <Sidebar route={route} go={go} />
        <main className="main">
          <AppTopbar route={route} />
          <IgualdadShell>
            <div className="content fade-in" key={route}>
              <ExigeEmpresa><Page route={route} /></ExigeEmpresa>
            </div>
          </IgualdadShell>
        </main>
      </div>
    </SvptProvider>
  );
}

/* Puerta de sesion: arranque, login a pantalla completa, o la aplicacion. */
function Gate() {
  const { session } = useIgualdad();

  if (session.booting) {
    return (
      <div className="rb-boot">
        <Loader2 size={26} className="rb-boot-spin" />
        <span>Cargando…</span>
      </div>
    );
  }

  if (!session.user) {
    return <IgualdadLogin onDone={() => navigate("/dashboard")} />;
  }

  return <AppAutenticada />;
}

/* Redirige a la seleccion de empresa si aun no hay una activa. */
function ExigeEmpresa({ children }) {
  const { session } = useIgualdad();
  const route = (window.location.hash || "").replace(/^#/, "");
  useEffect(() => {
    if (!session.cliente && !route.startsWith("/empresas")) navigate("/empresas");
  }, [session.cliente, route]);
  return children;
}

function App() {
  return (
    <SessionProvider>
      <Gate />
    </SessionProvider>
  );
}

export { App, NAV, Sidebar };
export default App;